import React, { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import {
  connectWebSerial,
  disconnectWebSerial,
  getWebSerialStatus,
  isWebSerialSupported,
  onWebSerialData,
  onWebSerialStatus,
  buildProtocolCommand,
  sendProtocolCommandViaWebSerial,
  writeWebSerialText,
} from "../services/webSerial";
import FrameInspector from "./FrameInspector";

const API = process.env.REACT_APP_API_BASE || "http://localhost:3001";

const consoleStyle = {
  background: "#0b0f14",
  border: "1px solid rgba(0, 223, 255, 0.24)",
  color: "#d6faff",
  borderRadius: 14,
  padding: 12,
  height: 390,
  overflowY: "auto",
  fontFamily: "Consolas, monospace",
  fontSize: 13,
  whiteSpace: "pre-wrap",
};

const panelStyle = {
  width: "100%",
  minHeight: 560,
  display: "flex",
  flexDirection: "column",
  gap: 14,
};

const cardStyle = {
  border: "1px solid rgba(255,255,255,.11)",
  background: "rgba(255,255,255,.045)",
  borderRadius: 18,
  padding: 16,
};

const inputStyle = {
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid rgba(255,255,255,.16)",
  background: "#171717",
  color: "#fff",
  outline: "none",
};

const buttonStyle = {
  padding: "10px 13px",
  borderRadius: 10,
  border: "1px solid rgba(0,223,255,.35)",
  background: "rgba(0,223,255,.12)",
  color: "#00dfff",
  fontWeight: 800,
  cursor: "pointer",
};

const successButtonStyle = {
  ...buttonStyle,
  border: "1px solid rgba(34,197,94,.38)",
  background: "rgba(34,197,94,.13)",
  color: "#86efac",
};

const dangerButtonStyle = {
  ...buttonStyle,
  border: "1px solid rgba(251,113,133,.35)",
  background: "rgba(251,113,133,.12)",
  color: "#fb7185",
};

const noteStyle = {
  margin: "10px 0 0",
  color: "#facc15",
  fontSize: 13,
  lineHeight: 1.45,
};

const SerialMonitor = () => {
  const [portPath, setPortPath] = useState("COM3");
  const [baudRate, setBaudRate] = useState(115200);
  const [ports, setPorts] = useState([]);
  const [status, setStatus] = useState({ connected: false, path: null, baudRate: null });
  const [webStatus, setWebStatus] = useState(getWebSerialStatus());
  const [logs, setLogs] = useState([]);
  const [command, setCommand] = useState("");
  const [lastPreviewFrame, setLastPreviewFrame] = useState(null);
  const [protocolFrames, setProtocolFrames] = useState([]);
  const socketRef = useRef(null);
  const consoleRef = useRef(null);

  const addLog = (message) => {
    const time = new Date().toLocaleTimeString();
    setLogs((prev) => [...prev, `[${time}] ${message}`].slice(-700));
  };

  const fetchStatus = async () => {
    try {
      const res = await fetch(`${API}/serial/status`);
      const data = await res.json();
      if (data?.ok) {
        setStatus({ connected: data.connected, path: data.path, baudRate: data.baudRate });
        if (data.path) setPortPath(data.path);
        if (data.baudRate) setBaudRate(data.baudRate);
      }
    } catch {
      setStatus({ connected: false, path: null, baudRate: null });
    }
  };

  const fetchPorts = async () => {
    try {
      const res = await fetch(`${API}/serial/ports`);
      const data = await res.json();
      const list = Array.isArray(data) ? data : data?.ports || [];
      setPorts(list);
      if (list.length && !portPath) setPortPath(list[0].path);
      addLog(`Puertos detectados por backend EC2/local: ${list.length}`);
    } catch (error) {
      addLog(`Error listando puertos backend: ${error.message}`);
    }
  };

  useEffect(() => {
    const socket = io(API);
    socketRef.current = socket;

    socket.on("connect", () => addLog("Socket conectado al backend"));
    socket.on("disconnect", () => addLog("Socket desconectado"));

    socket.on("serial:status", (data) => {
      setStatus({ connected: data.connected, path: data.path, baudRate: data.baudRate });
      addLog(data.connected ? `Serial backend conectado: ${data.path} @ ${data.baudRate}` : "Serial backend desconectado");
    });

    socket.on("serial:data", ({ path, data, hex }) => {
      addLog(`< backend ${path || "serial"} ASCII: ${data}`);
      if (hex) addLog(`< backend ${path || "serial"} HEX: ${hex}`);
    });

    socket.on("serial:tx", ({ path, mode, data, frameHex, label, seq }) => {
      if (mode === "binary") {
        addLog(`> backend ${path || "serial"} FRAME seq=${seq} ${label || ""}: ${frameHex}`);
        setProtocolFrames((current) => [{ direction: "TX", label: label || `SEQ ${seq}`, frameHex }, ...current].slice(0, 30));
      } else {
        addLog(`> backend ${path || "serial"}: ${data}`);
      }
    });

    socket.on("serial:frame", ({ path, subsys, cmd, commandName, seq, len, payloadHex, frameHex, details }) => {
      setProtocolFrames((current) => [{ direction: "RX", label: `${commandName || "TRAMA"} · SEQ ${seq}`, frameHex }, ...current].slice(0, 30));
      addLog(
        `< backend ${path || "serial"} TRAMA SUBSYS=0x${Number(subsys)
          .toString(16)
          .padStart(2, "0")
          .toUpperCase()} CMD=0x${Number(cmd)
          .toString(16)
          .padStart(2, "0")
          .toUpperCase()} ${commandName || ""} SEQ=${seq} LEN=${len}: ${frameHex} PAYLOAD=${payloadHex || "(vacío)"}${
            details?.requestCommand !== undefined ? ` REQUEST_CMD=0x${Number(details.requestCommand).toString(16).padStart(2, "0").toUpperCase()}` : ""
          }${
            details?.imageChunk
              ? ` IMAGEN=${details.imageChunk.algorithmName} CHUNK=${details.imageChunk.chunkIndex + 1}/${details.imageChunk.totalChunks} REQUEST_SEQ=${details.imageChunk.requestSeq}`
              : ""
          }`
      );
    });

    socket.on("serial:frame-error", ({ path, error, frameHex, discardedHex }) => {
      addLog(
        `[ERR trama ${path || "serial"}] ${error}: ${frameHex || discardedHex || "sin datos"}`
      );
    });

    socket.on("serial:error", ({ path, error }) => {
      addLog(`[ERR backend ${path || "serial"}] ${error}`);
    });

    const offWebData = onWebSerialData((event) => {
      if (event.direction === "rx") {
        addLog(`< tarjeta local ASCII: ${event.data || ""}`);
        addLog(`< tarjeta local HEX: ${event.hex}`);
      } else if (event.direction === "frame") {
        setProtocolFrames((current) => [{ direction: "RX", label: `${event.commandName} · SEQ ${event.seq}`, frameHex: event.frameHex }, ...current].slice(0, 30));
        addLog(
          `< tarjeta local TRAMA ${event.commandName} SUBSYS=0x${Number(event.subsys)
            .toString(16)
            .padStart(2, "0")
            .toUpperCase()} SEQ=${event.seq} LEN=${event.len}: ${event.frameHex} PAYLOAD=${event.payloadHex || "(vacío)"}${
              event.details?.requestCommand !== undefined
                ? ` REQUEST_CMD=0x${Number(event.details.requestCommand).toString(16).padStart(2, "0").toUpperCase()}`
                : ""
            }${
              event.details?.imageChunk
                ? ` IMAGEN=${event.details.imageChunk.algorithmName} CHUNK=${event.details.imageChunk.chunkIndex + 1}/${event.details.imageChunk.totalChunks} REQUEST_SEQ=${event.details.imageChunk.requestSeq}`
                : ""
            }`
        );
      } else if (event.direction === "frame-error") {
        addLog(`[ERR trama local] ${event.error}: ${event.frameHex || event.discardedHex || "sin datos"}`);
      } else if (event.direction === "tx") {
        addLog(`> tarjeta local ${event.label || "frame"}: ${event.hex}`);
        if (String(event.hex || "").startsWith("7E")) {
          setProtocolFrames((current) => [{ direction: "TX", label: event.label || "Trama", frameHex: event.hex }, ...current].slice(0, 30));
        }
      } else if (event.direction === "error") {
        addLog(`[ERR tarjeta local] ${event.error}`);
      }
    });

    const offWebStatus = onWebSerialStatus((nextStatus) => {
      setWebStatus(nextStatus);
      if (nextStatus.message) addLog(`Web Serial: ${nextStatus.message}`);
    });

    fetchStatus();
    fetchPorts();

    return () => {
      socket.disconnect();
      offWebData();
      offWebStatus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (consoleRef.current) {
      consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
    }
  }, [logs]);

  const connectBackendSerial = async () => {
    try {
      addLog(`Abriendo serial del backend ${portPath} @ ${baudRate}...`);
      const res = await fetch(`${API}/serial/open`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ path: portPath, baudRate: Number(baudRate) }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || `Error HTTP ${res.status}`);
      }

      setStatus({ connected: data.connected, path: data.path, baudRate: data.baudRate });
      addLog("Puerto serial del backend abierto correctamente");
    } catch (error) {
      addLog(`Error al abrir puerto del backend: ${error.message}`);
    }
  };

  const autoOpen = async () => {
    try {
      addLog("Intentando auto-open desde backend.config.json...");
      const res = await fetch(`${API}/serial/auto-open`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ path: portPath, baudRate: Number(baudRate) }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || `Error HTTP ${res.status}`);
      }

      setStatus({ connected: data.connected, path: data.path, baudRate: data.baudRate });
      addLog("Auto-open backend correcto");
    } catch (error) {
      addLog(`Error auto-open backend: ${error.message}`);
    }
  };

  const disconnectBackendSerial = async () => {
    try {
      const res = await fetch(`${API}/serial/close`, { method: "POST" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) throw new Error(data?.error || `Error HTTP ${res.status}`);
      setStatus({ connected: false, path: null, baudRate: null });
      addLog("Puerto serial del backend cerrado");
    } catch (error) {
      addLog(`Error al cerrar serial backend: ${error.message}`);
    }
  };

  const connectLocalCard = async () => {
    try {
      await connectWebSerial({ baudRate: Number(baudRate) });
      setWebStatus(getWebSerialStatus());
      addLog("Tarjeta local conectada por Web Serial");
    } catch (error) {
      addLog(`Error Web Serial: ${error.message}`);
    }
  };

  const disconnectLocalCard = async () => {
    try {
      await disconnectWebSerial();
      setWebStatus(getWebSerialStatus());
    } catch (error) {
      addLog(`Error cerrando Web Serial: ${error.message}`);
    }
  };

  const sendCommandBackend = async () => {
    if (!command.trim()) return;

    try {
      const res = await fetch(`${API}/serial/write`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data: command.replace(/[\r\n]+$/, "") }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) throw new Error(data?.error || `Error HTTP ${res.status}`);
      setCommand("");
    } catch (error) {
      addLog(`Error enviando comando al backend: ${error.message}`);
    }
  };

  const sendCommandLocal = async () => {
    if (!command.trim()) return;

    try {
      await writeWebSerialText(`${command.replace(/[\r\n]+$/, "")}\r\n`);
      setCommand("");
    } catch (error) {
      addLog(`Error enviando texto a tarjeta local: ${error.message}`);
    }
  };

  const previewTestFrame = async () => {
    try {
      const data = await buildProtocolCommand(API, { subsystem: "EPS", command: "PING" });
      setLastPreviewFrame(data);
      addLog(`Preview PING EPS: ${data.frameHex}`);
      return data;
    } catch (error) {
      addLog(`Error preview: ${error.message}`);
    }
  };

  const sendPreviewToLocalCard = async () => {
    try {
      const result = await sendProtocolCommandViaWebSerial(API, { subsystem: "EPS", command: "PING" }, { expectedCommand: "PONG", timeoutMs: 3500 });
      setLastPreviewFrame(result);
      addLog(`PING EPS enviado; respuesta ${result.response?.commandName || "pendiente"}`);
    } catch (error) {
      addLog(`Error enviando preview a tarjeta local: ${error.message}`);
    }
  };

  const webSerialNote = !isWebSerialSupported()
    ? "Web Serial no está disponible. Para usarlo desde IP pública necesitas abrir la app con HTTPS en Chrome/Edge. En HTTP público el navegador lo bloquea."
    : !webStatus.secureContext
      ? "Web Serial requiere HTTPS o localhost. Con http://IP_PUBLICA no se podrá abrir la tarjeta local hasta configurar HTTPS o usar el modo temporal de Chrome para laboratorio."
      : "Web Serial disponible para escribir al puerto local de esta laptop.";

  return (
    <div style={panelStyle}>
      <div style={cardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
          <div>
            <strong style={{ color: "#00dfff", fontSize: 18 }}>Monitor Serial</strong>
            <p style={{ color: "#b8c2cc", margin: "6px 0 0" }}>
              Backend: {API} · Serial backend: {status.connected ? `${status.path} @ ${status.baudRate}` : "sin conexión"}
            </p>
            <p style={{ color: "#b8c2cc", margin: "6px 0 0" }}>
              Tarjeta local Web Serial: {webStatus.connected ? "conectada" : "sin conexión"}
            </p>
            <p style={noteStyle}>{webSerialNote}</p>
            <p style={{ color: "#b8c2cc", margin: "7px 0 0", fontSize: 12 }}>
              Tramas binarias: sin CR/LF. Texto manual: termina en CRLF.
            </p>
          </div>
          <button type="button" onClick={fetchStatus} style={buttonStyle}>Actualizar estado</button>
        </div>
      </div>

      <div style={cardStyle}>
        <strong style={{ color: "#00dfff", display: "block", marginBottom: 10 }}>Modo tarjeta local desde navegador</strong>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <input value={baudRate} onChange={(e) => setBaudRate(Number(e.target.value))} type="number" placeholder="115200" style={{ ...inputStyle, width: 120 }} />
          <button type="button" onClick={connectLocalCard} style={successButtonStyle}>Conectar tarjeta local</button>
          <button type="button" onClick={disconnectLocalCard} style={dangerButtonStyle}>Desconectar local</button>
          <button type="button" onClick={previewTestFrame} style={buttonStyle}>Preview PING EPS</button>
          <button type="button" onClick={sendPreviewToLocalCard} style={successButtonStyle}>Enviar PING EPS</button>
        </div>
        {lastPreviewFrame?.frameHex && <div style={{ marginTop: 12 }}><FrameInspector hex={lastPreviewFrame.frameHex} direction="TX" label={`PING EPS · SEQ ${lastPreviewFrame.seq}`} /></div>}
      </div>

      <div style={cardStyle}>
        <strong style={{ color: "#00dfff", display: "block", marginBottom: 10 }}>Modo serial del backend</strong>
        <p style={{ color: "#b8c2cc", marginTop: 0, fontSize: 13 }}>
          Este modo sirve cuando el backend corre en la misma máquina que la tarjeta. En EC2 no verá el COM de tu laptop.
        </p>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <select value={portPath} onChange={(e) => setPortPath(e.target.value)} style={{ ...inputStyle, minWidth: 190 }}>
            <option value={portPath}>{portPath || "Selecciona puerto"}</option>
            {ports.map((port) => (
              <option key={port.path} value={port.path}>
                {port.path} {port.manufacturer ? `· ${port.manufacturer}` : ""}
              </option>
            ))}
          </select>

          <input value={portPath} onChange={(e) => setPortPath(e.target.value)} placeholder="COM3 o /dev/ttyUSB0" style={{ ...inputStyle, width: 170 }} />
          <input value={baudRate} onChange={(e) => setBaudRate(Number(e.target.value))} type="number" placeholder="115200" style={{ ...inputStyle, width: 120 }} />

          <button type="button" onClick={fetchPorts} style={buttonStyle}>Listar puertos backend</button>
          <button type="button" onClick={connectBackendSerial} style={buttonStyle}>Conectar backend</button>
          <button type="button" onClick={autoOpen} style={buttonStyle}>Auto-open backend</button>
          <button type="button" onClick={disconnectBackendSerial} style={dangerButtonStyle}>Desconectar backend</button>
        </div>
      </div>

      <div style={consoleStyle} ref={consoleRef}>
        {logs.length ? logs.join("\n") : "Esperando actividad serial..."}
      </div>

      <div style={cardStyle}>
        <strong style={{ color: "#00dfff", display: "block", marginBottom: 10 }}>Inspector binario TX/RX</strong>
        <p style={{ color: "#b8c2cc", marginTop: 0, fontSize: 13 }}>Expande una trama para ver cada byte, su significado y la validación del CRC8.</p>
        <div style={{ display: "grid", gap: 8 }}>
          {protocolFrames.length ? protocolFrames.map((frame, index) => <FrameInspector key={`${frame.direction}-${frame.frameHex}-${index}`} {...frame} />) : <span style={{ color: "#8fa2b4" }}>Aún no hay tramas binarias completas.</span>}
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <input
            value={command}
            onChange={(e) => setCommand(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") sendCommandLocal();
            }}
            placeholder="Comando manual para UART"
            style={{ ...inputStyle, flex: 1, minWidth: 260 }}
          />
          <button type="button" onClick={sendCommandLocal} style={successButtonStyle}>Enviar texto local</button>
          <button type="button" onClick={sendCommandBackend} style={buttonStyle}>Enviar texto backend</button>
          <button type="button" onClick={() => setLogs([])} style={dangerButtonStyle}>Limpiar</button>
        </div>
      </div>
    </div>
  );
};

export default SerialMonitor;
